import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Users,
  Eye,
  MousePointerClick,
  Apple,
  Play,
  Globe,
  RefreshCw,
  LogOut,
  BookOpen,
  ListOrdered,
  Search,
  Trash2,
  Check,
  CalendarClock,
  Mail,
  Share2,
  BarChart3,
  Instagram,
  AlertTriangle,
} from "lucide-react";
import Seo from "../components/Seo";
import TrafficSources from "../components/TrafficSources";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [
  { days: 1, label: "Today" },
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

const Stat = ({ icon: Icon, label, value, sub }) => (
  <div className="adm-stat">
    <span className="adm-stat-ico">
      <Icon size={16} aria-hidden="true" />
    </span>
    <div>
      <b>{Number(value || 0).toLocaleString()}</b>
      <span>{label}</span>
      {sub && <em>{sub}</em>}
    </div>
  </div>
);

const AdminDashboard = ({ user }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [sources, setSources] = useState(null);
  const [deletions, setDeletions] = useState(null);
  const [messages, setMessages] = useState(null);
  const [fb, setFb] = useState(null);
  const [ig, setIg] = useState(null);
  const [igAccount, setIgAccount] = useState("");
  const [igSaving, setIgSaving] = useState(false);
  const [igMsg, setIgMsg] = useState("");
  const [articles, setArticles] = useState(null);
  const [rpm, setRpm] = useState(
    () => localStorage.getItem("inz_rpm") || "1.50"
  );
  const [resetText, setResetText] = useState("");
  const [resetMsg, setResetMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(
    (range) => {
      setLoading(true);
      setError("");
      axios
        .get(`${API}/admin/analytics/summary`, {
          params: { days: range },
          withCredentials: true,
        })
        .then((r) => setData(r.data))
        .catch((e) =>
          setError(e?.response?.data?.detail || "Could not load analytics.")
        )
        .finally(() => setLoading(false));
      axios
        .get(`${API}/admin/analytics/sources`, {
          params: { days: range },
          withCredentials: true,
        })
        .then((r) => setSources(r.data))
        .catch(() => setSources(null));
      axios
        .get(`${API}/admin/deletion-requests`, {
          params: { limit: 50 },
          withCredentials: true,
        })
        .then((r) => setDeletions(r.data))
        .catch(() => setDeletions(null));
      axios
        .get(`${API}/admin/contact-messages`, {
          params: { limit: 50 },
          withCredentials: true,
        })
        .then((r) => setMessages(r.data))
        .catch(() => setMessages(null));
      axios
        .get(`${API}/admin/facebook/status`, { withCredentials: true })
        .then((r) => setFb(r.data))
        .catch(() => setFb(null));
      axios
        .get(`${API}/admin/instagram/status`, { withCredentials: true })
        .then((r) => setIg(r.data))
        .catch(() => setIg(null));
      axios
        .get(`${API}/admin/analytics/articles`, {
          params: { days: range, rpm: parseFloat(rpm) || 0 },
          withCredentials: true,
        })
        .then((r) => setArticles(r.data))
        .catch(() => setArticles(null));
    },
    [rpm]
  );

  useEffect(() => {
    load(days);
  }, [days, load]);

  const markDone = (ticket) => {
    axios
      .post(`${API}/admin/deletion-requests/${ticket}/done`, {}, { withCredentials: true })
      .then(() => load(days))
      .catch(() => {});
  };

  const markMessageDone = (ref) => {
    axios
      .post(`${API}/admin/contact-messages/${ref}/done`, {}, { withCredentials: true })
      .then(() => load(days))
      .catch(() => {});
  };

  const saveIgAccount = () => {
    setIgSaving(true);
    setIgMsg("");
    axios
      .post(
        `${API}/admin/instagram/account`,
        { ig_id: igAccount.trim() },
        { withCredentials: true }
      )
      .then((r) => {
        setIgMsg(`Connected to @${r.data.username} — the next article posts automatically.`);
        setIgAccount("");
        load(days);
      })
      .catch((e) =>
        setIgMsg(e?.response?.data?.detail || "Could not connect that account.")
      )
      .finally(() => setIgSaving(false));
  };


  const resetTracking = () => {
    setResetMsg("");
    axios
      .post(`${API}/admin/analytics/reset`, {}, { withCredentials: true })
      .then((r) => {
        setResetMsg(`Done — ${Number(r.data.deleted).toLocaleString()} events deleted. Counts start from zero.`);
        setResetText("");
        load(days);
      })
      .catch((e) =>
        setResetMsg(e?.response?.data?.detail || "Could not reset tracking data.")
      );
  };

  const logout = () => {
    axios
      .post(`${API}/auth/logout`, {}, { withCredentials: true })
      .finally(() => window.location.replace("/admin/login"));
  };

  const t = data?.totals || {};
  const countries = (data?.countries || []).filter((c) => c.events > 0);
  const maxCountry = countries.length
    ? Math.max(...countries.map((c) => c.events))
    : 1;

  return (
    <>
      <Seo
        title="Admin dashboard | InZoRAH"
        description="Private analytics dashboard."
        path="/admin"
        noindex
      />
      <div className="adm-shell">
        <header className="adm-top">
          <div>
            <p className="adm-eyebrow">InZoRAH admin</p>
            <h1>Traffic &amp; clicks</h1>
          </div>
          <div className="adm-top-right">
            <div className="adm-range" role="group" aria-label="Date range">
              {RANGES.map((r) => (
                <button
                  key={r.days}
                  type="button"
                  className={r.days === days ? "is-on" : ""}
                  onClick={() => setDays(r.days)}
                  data-testid={`range-${r.days}-button`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <button type="button" className="adm-icon-btn" onClick={() => load(days)} data-testid="refresh-button">
              <RefreshCw size={15} aria-hidden="true" /> Refresh
            </button>
            <div className="adm-user">
              {user?.picture ? (
                <img src={user.picture} alt="" width="28" height="28" />
              ) : null}
              <span>{user?.email}</span>
              <button type="button" onClick={logout} title="Sign out">
                <LogOut size={15} aria-hidden="true" />
              </button>
            </div>
          </div>
        </header>

        {error && <p className="adm-error adm-block">{error}</p>}
        {loading && !data && <p className="adm-loading">Loading analytics…</p>}

        {data && (
          <main className="adm-main">
            <section className="adm-today" data-testid="today-panel">
              <p className="adm-today-h">
                <CalendarClock size={15} aria-hidden="true" /> Today ·{" "}
                {data.today?.day || ""}
              </p>
              <div className="adm-today-grid">
                <div data-testid="today-visitors">
                  <b>{Number(data.today?.unique_visitors || 0).toLocaleString()}</b>
                  <span>Visitors today</span>
                </div>
                <div data-testid="today-views">
                  <b>{Number(data.today?.page_views || 0).toLocaleString()}</b>
                  <span>Page views today</span>
                </div>
                <div data-testid="today-clicks">
                  <b>{Number(data.today?.clicks || 0).toLocaleString()}</b>
                  <span>Clicks today</span>
                </div>
                <div data-testid="today-events">
                  <b>{Number(data.today?.total_events || 0).toLocaleString()}</b>
                  <span>Events today</span>
                </div>
              </div>
            </section>

            <section className="adm-stats">
              <Stat icon={Eye} label="Page views" value={t.page_views} />
              <Stat icon={Users} label="Unique visitors" value={t.unique_visitors} />
              <Stat icon={MousePointerClick} label="Tracked clicks" value={t.clicks} />
              <Stat icon={ListOrdered} label="Ingredients A-Z clicks" value={t.ingredients_az} />
              <Stat icon={BookOpen} label="Ingredient blog clicks" value={t.ingredient_blog} />
              <Stat icon={Apple} label="App Store clicks" value={t.app_store} />
              <Stat icon={Play} label="Google Play clicks" value={t.google_play} />
              <Stat
                icon={Globe}
                label="Countries seen"
                value={countries.length}
                sub={`since ${data.since}`}
              />
            </section>

            <section className="adm-card">
              <h2>Visitors over time</h2>
              <p className="adm-sub">
                Page views and unique visitors per day, last {data.range_days} days.
              </p>
              <div className="adm-chart">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data.timeseries}>
                    <CartesianGrid stroke="#EDE9FA" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "#5B5570" }}
                      stroke="#D8D0F4"
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#5B5570" }}
                      stroke="#D8D0F4"
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: "1px solid #E2DDF5",
                        fontSize: 13,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line
                      type="monotone"
                      dataKey="views"
                      name="Page views"
                      stroke="#6C4DF6"
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="visitors"
                      name="Unique visitors"
                      stroke="#F5A623"
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>

            <div className="adm-two">
              <section className="adm-card">
                <h2>Clicks by button</h2>
                <p className="adm-sub">What people actually tap.</p>
                <div className="adm-chart">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.clicks} layout="vertical" margin={{ left: 8 }}>
                      <CartesianGrid stroke="#EDE9FA" horizontal={false} />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11, fill: "#5B5570" }}
                        stroke="#D8D0F4"
                        allowDecimals={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={132}
                        tick={{ fontSize: 11, fill: "#3A3358" }}
                        stroke="#D8D0F4"
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 12,
                          border: "1px solid #E2DDF5",
                          fontSize: 13,
                        }}
                      />
                      <Bar
                        dataKey="count"
                        name="Clicks"
                        fill="#6C4DF6"
                        radius={[0, 6, 6, 0]}
                        barSize={16}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>

              <section className="adm-card">
                <h2>Where they are</h2>
                <p className="adm-sub">
                  Country of every visit and click (from the edge network header).
                </p>
                <div className="adm-chart">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={countries.slice(0, 10)}
                      layout="vertical"
                      margin={{ left: 8 }}
                    >
                      <CartesianGrid stroke="#EDE9FA" horizontal={false} />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11, fill: "#5B5570" }}
                        stroke="#D8D0F4"
                        allowDecimals={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={132}
                        tick={{ fontSize: 11, fill: "#3A3358" }}
                        stroke="#D8D0F4"
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 12,
                          border: "1px solid #E2DDF5",
                          fontSize: 13,
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar
                        dataKey="events"
                        name="All events"
                        fill="#6C4DF6"
                        radius={[0, 6, 6, 0]}
                        barSize={12}
                      />
                      <Bar
                        dataKey="clicks"
                        name="Clicks"
                        fill="#F5A623"
                        radius={[0, 6, 6, 0]}
                        barSize={12}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {countries.length === 0 && (
                  <p className="adm-empty">No country data yet.</p>
                )}
              </section>
            </div>

            <h2 className="adm-section-h">Traffic sources</h2>
            <TrafficSources data={sources} />

            <div className="adm-two">
              <section className="adm-card">
                <h2>Top pages</h2>
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th scope="col">Path</th>
                      <th scope="col">Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.top_pages || []).map((p) => (
                      <tr key={p.path}>
                        <td>{p.path}</td>
                        <td>{p.views.toLocaleString()}</td>
                      </tr>
                    ))}
                    {!data.top_pages?.length && (
                      <tr>
                        <td colSpan="2" className="adm-empty">
                          No page views in this range yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </section>

              <section className="adm-card">
                <h2>
                  <Search size={15} aria-hidden="true" /> Glossary searches
                </h2>
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th scope="col">Search term</th>
                      <th scope="col">Times</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.top_searches || []).map((s) => (
                      <tr key={s.term}>
                        <td>{s.term}</td>
                        <td>{s.count}</td>
                      </tr>
                    ))}
                    {!data.top_searches?.length && (
                      <tr>
                        <td colSpan="2" className="adm-empty">
                          Nothing searched yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </section>
            </div>

            <section className="adm-card">
              <h2>
                <Trash2 size={15} aria-hidden="true" /> Data deletion requests
                {deletions?.open ? (
                  <span className="adm-badge">{deletions.open} open</span>
                ) : null}
              </h2>
              <p className="adm-sub">
                Submitted from /data-deletion. Action within 30 days, then mark done.
              </p>
              <table className="adm-table">
                <thead>
                  <tr>
                    <th scope="col">Ticket</th>
                    <th scope="col">Email</th>
                    <th scope="col">Scope</th>
                    <th scope="col">Platform</th>
                    <th scope="col">Received</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(deletions?.items || []).map((d) => (
                    <tr key={d.ticket}>
                      <td className="adm-mono">{d.ticket}</td>
                      <td>{d.email}</td>
                      <td>{d.scope}</td>
                      <td>{d.platform || "-"}</td>
                      <td>{(d.created_at || "").slice(0, 10)}</td>
                      <td>
                        {d.status === "done" ? (
                          <span className="adm-done-tag">done</span>
                        ) : (
                          <button
                            type="button"
                            className="adm-mark-done"
                            onClick={() => markDone(d.ticket)}
                          >
                            <Check size={12} aria-hidden="true" /> Mark done
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!deletions?.items?.length && (
                    <tr>
                      <td colSpan="6" className="adm-empty">
                        No deletion requests yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>

            <section className="adm-card">
              <h2>Every event recorded</h2>
              <p className="adm-sub">
                New event names appear here automatically, so future tracking needs no
                dashboard changes.
              </p>
              <div className="adm-events">
                {(data.events_breakdown || []).map((e) => (
                  <span key={e.event}>
                    {e.event} <em>{e.count.toLocaleString()}</em>
                  </span>
                ))}
                {!data.events_breakdown?.length && (
                  <p className="adm-empty">No events in this range yet.</p>
                )}
              </div>
              <p className="adm-content-line">
                Content published: {data.content?.blog_posts} blog posts ·{" "}
                {data.content?.glossary_entries} glossary entries ·{" "}
                {data.content?.categories} categories
              </p>
            </section>
            <section className="adm-card" data-testid="messages-panel">
              <h2>
                <Mail size={15} aria-hidden="true" /> Contact messages
                {messages?.new ? (
                  <span className="adm-badge">{messages.new} new</span>
                ) : null}
              </h2>
              <p className="adm-sub">
                Sent from /contact. Every message is also emailed to support@inzorah.com —
                this is your backup copy, so nothing is ever lost.
              </p>
              <table className="adm-table">
                <thead>
                  <tr>
                    <th scope="col">Ref</th>
                    <th scope="col">From</th>
                    <th scope="col">Topic</th>
                    <th scope="col">Message</th>
                    <th scope="col">Emailed</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(messages?.items || []).map((m) => (
                    <tr key={m.ref}>
                      <td className="adm-mono">{m.ref}</td>
                      <td>
                        {m.name}
                        <br />
                        <a href={`mailto:${m.email}`}>{m.email}</a>
                      </td>
                      <td>{m.subject}</td>
                      <td className="adm-msg-cell">{m.message}</td>
                      <td>{m.emailed ? "yes" : "no"}</td>
                      <td>
                        {m.status === "done" ? (
                          <span className="adm-done-tag">done</span>
                        ) : (
                          <button
                            type="button"
                            className="adm-mark-done"
                            onClick={() => markMessageDone(m.ref)}
                          >
                            <Check size={12} aria-hidden="true" /> Mark done
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!messages?.items?.length && (
                    <tr>
                      <td colSpan="6" className="adm-empty">
                        No messages yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>

            <section className="adm-card" data-testid="facebook-panel">
              <h2>
                <Share2 size={15} aria-hidden="true" /> Facebook auto-posting
              </h2>
              {fb ? (
                <>
                  <p className="adm-sub">
                    {fb.configured ? (
                      <>
                        Page <b>{fb.page_name || fb.page_id}</b> ·{" "}
                        {fb.token_valid ? "token valid" : "token problem"}
                        {fb.never_expires ? " · never expires" : ""} ·{" "}
                        {fb.autopost_enabled ? "photo posts ON" : "photo posts OFF"} ·{" "}
                        {fb.reels_enabled ? "reels ON" : "reels OFF"} ·{" "}
                        {fb.posted_total} posts, {fb.reels_total} reels
                        {fb.error ? ` · ${fb.error}` : ""}
                      </>
                    ) : (
                      "Not configured."
                    )}
                  </p>
                  <table className="adm-table">
                    <thead>
                      <tr>
                        <th scope="col">Article</th>
                        <th scope="col">Type</th>
                        <th scope="col">When</th>
                        <th scope="col">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(fb.shares || []).map((s) => (
                        <tr key={s.slug + s.channel}>
                          <td>{s.title || s.slug}</td>
                          <td>
                            {s.channel === "facebook_reel" ? "reel" : s.kind || "photo"}
                          </td>
                          <td>{(s.created_at || "").slice(0, 16).replace("T", " ")}</td>
                          <td>
                            {s.status === "posted" ? (
                              <span className="adm-done-tag">posted</span>
                            ) : (
                              <span title={s.error || ""}>{s.status}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {!fb.shares?.length && (
                        <tr>
                          <td colSpan="4" className="adm-empty">
                            Nothing shared yet — the next article posts automatically.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
              ) : (
                <p className="adm-sub">Loading…</p>
              )}
            </section>

            <section className="adm-card" data-testid="article-performance-panel">
              <h2>
                <BarChart3 size={15} aria-hidden="true" /> Article performance
              </h2>
              <p className="adm-sub">
                Traffic per article from our own page views. Earnings are{" "}
                <b>estimated</b> at the page RPM below — AdSense-reported revenue
                needs its Management API and only exists once ads serve.
              </p>
              <label className="adm-rpm">
                Page RPM (US$ per 1,000 views)
                <input
                  type="number"
                  step="0.10"
                  min="0"
                  value={rpm}
                  onChange={(e) => {
                    setRpm(e.target.value);
                    localStorage.setItem("inz_rpm", e.target.value);
                  }}
                  data-testid="rpm-input"
                />
              </label>
              {articles ? (
                <>
                  <p className="adm-sub" data-testid="article-totals">
                    {articles.total_articles} pages viewed ·{" "}
                    {articles.total_views.toLocaleString()} views
                    {articles.total_est_revenue != null
                      ? ` · ~$${articles.total_est_revenue.toFixed(2)} estimated`
                      : ""}
                  </p>
                  <table className="adm-table">
                    <thead>
                      <tr>
                        <th scope="col">Article</th>
                        <th scope="col">Type</th>
                        <th scope="col">Views</th>
                        <th scope="col">Visitors</th>
                        <th scope="col">Est. revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {articles.articles.map((a) => (
                        <tr key={a.path}>
                          <td>
                            <a href={a.path} target="_blank" rel="noreferrer">
                              {a.title}
                            </a>
                          </td>
                          <td>{a.kind === "ingredient" ? "ingredient" : "post"}</td>
                          <td>{a.views.toLocaleString()}</td>
                          <td>{a.unique_visitors.toLocaleString()}</td>
                          <td>
                            {a.est_revenue != null ? `~$${a.est_revenue.toFixed(2)}` : "—"}
                          </td>
                        </tr>
                      ))}
                      {!articles.articles.length && (
                        <tr>
                          <td colSpan="5" className="adm-empty">
                            No article views in this range yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
              ) : (
                <p className="adm-sub">Loading…</p>
              )}
            </section>

            <section className="adm-card" data-testid="instagram-panel">
              <h2>
                <Instagram size={15} aria-hidden="true" /> Instagram auto-posting
              </h2>
              {ig ? (
                <>
                  <p className="adm-sub" data-testid="instagram-state">
                    {ig.configured ? (
                      <>
                        {ig.username ? <b>@{ig.username}</b> : ig.ig_id}
                        {ig.followers != null ? ` · ${ig.followers} followers` : ""} ·{" "}
                        {ig.autopost_enabled ? "photo posts ON" : "photo posts OFF"} ·{" "}
                        {ig.reels_enabled ? "reels ON" : "reels OFF"} ·{" "}
                        {ig.stories_enabled ? "stories ON" : "stories OFF"} ·{" "}
                        {ig.posted_total} posts, {ig.reels_total} reels,{" "}
                        {ig.stories_total} stories
                        {ig.quota_total != null
                          ? ` · ${ig.quota_usage}/${ig.quota_total} of today's quota`
                          : ""}
                        {ig.ig_id_source ? ` · account set in ${ig.ig_id_source}` : ""}
                        {ig.ig_id ? (
                          <>
                            {" · ID "}
                            <code data-testid="instagram-account-id">{ig.ig_id}</code>
                          </>
                        ) : (
                          ""
                        )}
                        {ig.error ? ` · ${ig.error}` : ""}
                      </>
                    ) : (
                      <>
                        Not connected yet — <b>{ig.blocker || "setup incomplete"}</b>.
                        {ig.missing_scopes?.length
                          ? " Regenerate the page token with those permissions ticked."
                          : ""}
                        {ig.error ? ` (${ig.error})` : ""}
                      </>
                    )}
                  </p>
                  {!ig.configured && (
                    <div className="adm-danger-row" data-testid="instagram-account-form">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={igAccount}
                        onChange={(e) => setIgAccount(e.target.value)}
                        placeholder="Instagram account ID (digits only)"
                        aria-label="Instagram account ID"
                        data-testid="instagram-account-input"
                      />
                      <button
                        type="button"
                        disabled={!igAccount.trim() || igSaving}
                        onClick={saveIgAccount}
                        data-testid="instagram-account-save"
                      >
                        {igSaving ? "Checking…" : "Connect"}
                      </button>
                    </div>
                  )}
                  {igMsg && (
                    <p className="adm-sub" data-testid="instagram-account-msg">
                      {igMsg}
                    </p>
                  )}
                  {ig.reels_blocker && (
                    <p className="adm-sub" data-testid="instagram-reels-blocker">
                      ⚠ {ig.reels_blocker}.
                    </p>
                  )}
                  {ig.configured && (
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th scope="col">Article</th>
                          <th scope="col">Type</th>
                          <th scope="col">When</th>
                          <th scope="col">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(ig.posts || []).map((s) => (
                          <tr key={s.slug + s.channel}>
                            <td>{s.title || s.slug}</td>
                            <td>
                              {s.channel === "instagram_reel"
                                ? "reel"
                                : s.channel === "instagram_story"
                                  ? "story"
                                  : "photo"}
                            </td>
                            <td>{(s.created_at || "").slice(0, 16).replace("T", " ")}</td>
                            <td>
                              {s.status === "posted" ? (
                                <span className="adm-done-tag">posted</span>
                              ) : (
                                <span title={s.error || ""}>{s.status}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {!ig.posts?.length && (
                          <tr>
                            <td colSpan="4" className="adm-empty">
                              Nothing shared yet — the next article posts automatically.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </>
              ) : (
                <p className="adm-sub">Loading…</p>
              )}
            </section>

            <section className="adm-card adm-danger" data-testid="reset-panel">
              <h2>
                <AlertTriangle size={15} aria-hidden="true" /> Danger zone
              </h2>
              <p className="adm-sub">
                Reset all tracking data back to zero. This permanently deletes every
                recorded page view, click and search. Contact messages and deletion
                requests are not touched.
              </p>
              <div className="adm-danger-row">
                <input
                  type="text"
                  value={resetText}
                  onChange={(e) => setResetText(e.target.value)}
                  placeholder="Type RESET to confirm"
                  aria-label="Type RESET to confirm"
                  data-testid="reset-confirm-input"
                />
                <button
                  type="button"
                  className="adm-danger-btn"
                  disabled={resetText.trim().toUpperCase() !== "RESET"}
                  onClick={resetTracking}
                  data-testid="reset-tracking-button"
                >
                  <Trash2 size={15} aria-hidden="true" /> Reset all tracking data
                </button>
              </div>
              {resetMsg && (
                <p className="adm-reset-msg" data-testid="reset-result">
                  {resetMsg}
                </p>
              )}
            </section>
          </main>
        )}
      </div>
    </>
  );
};

export default AdminDashboard;
